// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Linking } from 'react-native';

import { useI18n } from '@/i18n';
import {
  registerForPushNotifications,
  subscribeToNotificationTaps,
  subscribeToPushTokenChanges,
} from '@/services/pushNotifications';

/** The web listing: where market:// cannot open (no Play Store app), the browser still can. */
const PLAY_STORE_WEB_URL = 'https://play.google.com/store/apps/details?id=app.goffy';

/**
 * Registers this device for push once the user is signed in, re-registers when FCM rotates the
 * token, and opens what a tapped notification points at. A no-op on web.
 */
export function usePushNotifications(
  apiBaseUrl: string,
  userId: string | null | undefined,
  authToken: string | null | undefined,
  authChecked: boolean
) {
  const { t } = useI18n();
  const router = useRouter();
  // The access token refreshes often; registration keys on the user, and reads the latest token.
  const tokenRef = useRef(authToken);
  tokenRef.current = authToken;
  const channelName = t('push.channelName');

  useEffect(() => {
    if (!authChecked || !userId || !tokenRef.current) return undefined;
    const register = () => {
      if (tokenRef.current) void registerForPushNotifications(apiBaseUrl, tokenRef.current, channelName);
    };
    register();
    return subscribeToPushTokenChanges(register);
  }, [apiBaseUrl, authChecked, channelName, userId]);

  useEffect(
    () =>
      subscribeToNotificationTaps((action) => {
        if (action?.kind === 'route') {
          router.push(action.href as never);
        } else if (action?.kind === 'url') {
          Linking.openURL(action.url).catch(() => Linking.openURL(PLAY_STORE_WEB_URL).catch(() => undefined));
        }
      }),
    [router]
  );
}
